import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import WeekStrip from '../components/WeekStrip';
import { CalorieCard, NutritionCard } from '../components/DaySummary';
import MealList, { MealDetailSheet } from '../components/MealList';
import ActivityCard from '../components/ActivityCard';
import Icon from '../components/Icon';
import { DaySkeleton } from '../components/Skeletons';
import { NewTargetsCard } from '../components/Targets';
import { useCapture, useCapturedDay } from '../components/Capture';
import { today } from '../lib/dates';
import { isLogged } from '../lib/day';
import { AppContext, useApp } from '../lib/AppContext';
import { AnnouncementBanner } from '../components/Announcements';
import { GiftEndingBanner } from '../components/GiftEndedSheet';
import { TrialEndingBanner } from '../components/Trial';
import { TakeoutPrompt, TakeoutSheet, slotTarget, useTakeouts } from '../components/Takeout';
import { planDayIndex, plannedMeal } from '../lib/plans';
import DayTabs from '../components/DayTabs';
import AppointmentCard from '../components/AppointmentCard';
import WeekCard from '../components/WeekCard';
import { AbsenceSheet, ProposalSheet } from '../components/AppointmentSheets';
import { clock } from '../lib/schedule';
import { MEAL_TYPES } from '../lib/format';
import { requestReminderSync } from '../lib/reminders';

const SOON = 2 * 60 * 60 * 1000;
const SEEN_KEY = 'workoutTabSeen';
const readSeen = () => { try { return window.localStorage.getItem(SEEN_KEY); } catch { return null; } };
const writeSeen = (date) => { try { window.localStorage.setItem(SEEN_KEY, date); } catch { /* storage blocked: the dot just stays */ } };

function DayNotes({ day, onSave }) {
  const { editable } = useApp();
  const [notes, setNotes] = useState(day.notes || '');
  useEffect(() => setNotes(day.notes || ''), [day.date, day.notes]);

  if (!editable && !day.notes) return null;
  return (
    <section className="flex flex-col gap-2 rounded-[20px] bg-base-100 p-5">
      <h2 className="text-lg font-bold">Ghi chú</h2>
      {editable ? (
        <textarea className="textarea w-full rounded-[14px] border-line text-base" value={notes} aria-label="Ghi chú"
          placeholder="Hôm nay thế nào? Có gì muốn nhắn PT không…"
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => notes !== (day.notes || '') && onSave(notes)} />
      ) : (
        <p className="whitespace-pre-line text-sm">{day.notes}</p>
      )}
    </section>
  );
}

// Shown only while nothing is logged for the day.
function EmptyTip({ onClick }) {
  return (
    <button type="button" onClick={onClick}
      className="flex items-center gap-4 rounded-[20px] bg-[#e3f1e9] p-4 text-left text-[#17201b]">
      <span className="grid size-12 shrink-0 place-items-center rounded-[14px] bg-base-100 text-[#17804f]">
        <Icon name="camera" className="size-6" />
      </span>
      <span className="text-sm">Chụp ảnh hoặc mô tả món ăn — AI sẽ tính calo giúp bạn.</span>
    </button>
  );
}

// Staggered fade-in for the page's cards.
const rise = (i) => ({ className: 'rise', style: { animationDelay: `${i * 60}ms` } });

// Today (or a past day) in detail: this is where entries are reviewed and edited. The calorie card stays on
// top; under it two tabs, Ăn uống (nutrition, meals) and Tập luyện (the appointment with the coach, workouts,
// the week). Ăn uống opens first, Tập luyện when an appointment starts within 2 hours or from a reminder.
export default function DayPage() {
  const app = useApp();
  const { api, editable, goals } = app;
  const capture = useCapture();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') || today();
  // A day still to come can be opened (its scheduled sessions, its note) but not logged: the meal and workout
  // sections see it as read-only, so their add buttons don't show.
  const future = date > today();
  const logging = useMemo(() => (future ? { ...app, editable: false } : app), [future, app]);
  const [day, setDay] = useState(null);
  const [error, setError] = useState(null);
  const [openMealId, setOpenMealId] = useState(null);
  const [autoTab, setAutoTab] = useState(null);
  const [proposing, setProposing] = useState(null);
  const [reporting, setReporting] = useState(null);
  const [seen, setSeen] = useState(readSeen);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get(`/days/${date}`)
      .then((data) => !cancelled && setDay(data))
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [api, date]);

  useCapturedDay(date, setDay, { active: true });

  // Đặt ngoài (today only): a planned dish sets what the meal needs, else what's left of the day does.
  const [plan, setPlan] = useState(null);
  const [takeoutRequest, setTakeoutRequest] = useState(null);
  const takeouts = useTakeouts(date, { onDay: setDay });
  const orderOutToday = editable && date === today();
  useEffect(() => {
    if (orderOutToday) api.get(`/meal_plans/current?date=${date}`).then(setPlan).catch(() => setPlan(null));
  }, [api, orderOutToday, date]);
  const orderOut = (type) => {
    const planned = plannedMeal(plan, date, type);
    const dayIndex = plan ? planDayIndex(plan, date) : -1;
    const target = slotTarget(shown, type, shown.calorie_goal ?? goals.calories);
    setTakeoutRequest(planned
      ? { date, mealType: type, mealPlanId: plan.id, dayIndex, mealIndex: plan.plan.days[dayIndex].meals.indexOf(planned) }
      : { date, mealType: type, calories: target.calories, protein: target.protein });
  };

  // The landing page's old "Log meal" links used ?add=1; open the meal sheet for them.
  useEffect(() => {
    if (!editable || params.get('add') !== '1') return;
    capture.open('meal', { date });
    const next = new URLSearchParams(params);
    next.delete('add');
    setParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Every mutation endpoint returns the refreshed day.
  const mutate = useCallback(async (method, path, body) => setDay(await api[method](path, body)), [api]);
  const explicitTab = params.get('tab');
  const fromReminder = params.get('from') === 'reminder';
  const setDate = (value) => setParams({ ...(value === today() ? {} : { date: value }), ...(explicitTab ? { tab: explicitTab } : {}) });
  const shown = day?.date === date ? day : null;
  const appointments = shown?.appointments || [];
  const upcoming = appointments.find((a) => a.state !== 'absent' && a.state !== 'past');

  // Decided once per day shown: Tập luyện when an appointment is close or a reminder opened the app.
  useEffect(() => {
    if (!shown) return;
    const soon = upcoming && new Date(upcoming.starts_at) - Date.now() < SOON;
    setAutoTab(fromReminder || soon ? 'workout' : null);
  }, [shown?.date]); // eslint-disable-line react-hooks/exhaustive-deps
  const tab = explicitTab || autoTab || 'meals';
  const setTab = (value) => {
    const next = new URLSearchParams(params);
    next.set('tab', value);
    next.delete('from');
    setParams(next, { replace: true });
  };
  useEffect(() => {
    if (tab === 'workout' && date === today()) { writeSeen(date); setSeen(date); }
  }, [tab, date]);

  // What is due on the phone changes with what was logged today (no "did you train?" once a workout is in).
  const todayCounts = date === today() && shown ? `${shown.meals.length}:${shown.activities.length}` : null;
  useEffect(() => { if (editable && todayCounts) requestReminderSync(); }, [editable, todayCounts]);
  const replaceAppointment = (updated) => setDay((d) => ({ ...d, appointments: d.appointments.map((a) => (a.id === updated.id ? updated : a)) }));
  const openMeal = shown?.meals.find((m) => m.id === openMealId) || null;

  return (
    <div className="flex flex-col gap-4">
      <WeekStrip date={date} onChange={setDate} selectedLogged={shown ? isLogged(shown) : null} />
      <AnnouncementBanner screen="day" />
      {editable && <GiftEndingBanner />}
      {editable && <TrialEndingBanner />}
      {shown && takeouts.open.map((t) => <TakeoutPrompt key={t.id} takeout={t} onResolve={takeouts.resolve} />)}

      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!shown && !error && <DaySkeleton />}

      {shown && (
        <>
          {editable && date === today() && shown.target_change && (
            <div {...rise(0)}><NewTargetsCard key={shown.target_change.effective_from} change={shown.target_change} /></div>
          )}
          <div {...rise(0)}><CalorieCard day={shown} goal={shown.calorie_goal ?? goals.calories} /></div>
          <DayTabs tab={tab} onChange={setTab} dot={editable && date === today() && seen !== date && (!!upcoming || (appointments.some((a) => a.state !== 'absent') && shown.activities.length === 0))}
            summaries={{
              meals: `${MEAL_TYPES.filter((t) => shown.meals.some((m) => m.meal_type === t)).length}/4 bữa đã ghi`,
              workout: upcoming ? `Hẹn Coach ${clock(upcoming.starts_at)}` : shown.activities.length > 0 ? `Đã ghi ${shown.activities.length} buổi` : 'Chưa ghi',
            }} />
          {/* A coach's view: what the student keeps private is left out. */}
          {shown.hidden?.length > 0 && (
            <p className="flex items-center gap-2 rounded-[16px] bg-base-100 px-4 py-3 text-sm text-muted">
              <Icon name="lock" className="size-4 shrink-0" />
              Học viên không chia sẻ {shown.hidden.map((h) => (h === 'meals' ? 'bữa ăn' : 'tập luyện')).join(' và ')}
            </p>
          )}
          {editable && future && (
            <p className="flex items-center gap-2 rounded-[16px] bg-base-100 px-4 py-3 text-sm text-muted">
              <Icon name="calendar" className="size-4 shrink-0" />
              Ngày chưa tới: xem lịch tập và ghi chú. Ghi bữa ăn và buổi tập khi tới ngày nhé.
            </p>
          )}
          {tab === 'meals' ? (
            <AppContext.Provider value={logging}>
              <div {...rise(1)}><NutritionCard day={shown} /></div>
              {editable && !future && !isLogged(shown) && <div {...rise(2)}><EmptyTip onClick={() => capture.open('meal', { date })} /></div>}
              {!shown.hidden?.includes('meals') && (
                <div {...rise(3)}>
                  <MealList day={shown} onAdd={(mealType) => capture.open('meal', { date, mealType })} onOpen={(meal) => setOpenMealId(meal.id)}
                    onTakeout={orderOutToday ? orderOut : null} />
                </div>
              )}
            </AppContext.Provider>
          ) : (
            <>
              {editable && appointments.map((a) => (
                <div key={a.id} {...rise(1)}>
                  <AppointmentCard appointment={a} onPropose={setProposing} onAbsence={setReporting} onChanged={replaceAppointment}
                    onCheckedIn={() => api.get(`/days/${date}`).then(setDay).catch(() => {})} />
                </div>
              ))}
              {!shown.hidden?.includes('workouts') && (
                <AppContext.Provider value={logging}><div {...rise(2)}><ActivityCard day={shown} onAction={mutate} /></div></AppContext.Provider>
              )}
              <div {...rise(3)}><WeekCard date={date} workouts={shown.activities.length} /></div>
            </>
          )}
          <div {...rise(5)}>
            <DayNotes day={shown} onSave={(notes) => mutate('patch', `/days/${date}`, { notes })} />
          </div>
        </>
      )}

      <TakeoutSheet request={takeoutRequest} onClose={() => setTakeoutRequest(null)} onOrdered={takeouts.added} />
      <MealDetailSheet meal={openMeal} onAction={mutate} onClose={() => setOpenMealId(null)} />
      <ProposalSheet appointment={proposing} onClose={() => setProposing(null)} onDone={(updated) => { replaceAppointment(updated); setProposing(null); }} />
      <AbsenceSheet appointment={reporting} onClose={() => setReporting(null)} onDone={(updated) => { replaceAppointment(updated); setReporting(null); }} />
    </div>
  );
}
